import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import BidderCompanyForm, {
  EMPTY_COMPANY,
  validateCompany,
} from "./BidderCompanyForm";

// natural lot order: 2 before 10
const compareLotNumbers = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });

// The bidder's request to participate: the company form he knows from the
// terms, the lots he is interested in (the organiser decides) and a note.
// No terms are accepted here - that comes after approval.
const JoinRequestDialog = ({
  open,
  onClose,
  onSend,
  sending,
  prefillCompany,
  lots,
  auctionTitle,
  organiserName,
}) => {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [errors, setErrors] = useState({});
  const [allLots, setAllLots] = useState(true);
  const [chosen, setChosen] = useState([]);
  const [message, setMessage] = useState("");
  const [lotsError, setLotsError] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  // start from the pre-filled details each time it opens
  useEffect(() => {
    if (!open) return;
    setCompany({ ...EMPTY_COMPANY, ...(prefillCompany || {}) });
    setErrors({});
    setAllLots(true);
    setChosen([]);
    setMessage("");
    setLotsError("");
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => setIpAddress(data.ip))
      .catch(() => setIpAddress("Unknown"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sortedLots = [...(lots || [])].sort((a, b) =>
    compareLotNumbers(a.lotNumber, b.lotNumber)
  );

  const clearError = (field) =>
    setErrors((current) => {
      if (!current[field]) return current;
      const { [field]: _cleared, ...rest } = current;
      return rest;
    });

  const toggleLot = (eventItemId) => {
    setLotsError("");
    setChosen((current) =>
      current.includes(eventItemId)
        ? current.filter((id) => id !== eventItemId)
        : [...current, eventItemId]
    );
  };

  const send = () => {
    const found = validateCompany(company);
    setErrors(found);
    if (!allLots && chosen.length === 0) {
      setLotsError("Tick at least one lot, or choose All lots");
      return;
    }
    if (Object.keys(found).length > 0 || sending) return;
    onSend({
      company,
      interestedEventItemIds: allLots ? undefined : chosen,
      message: message.trim(),
      ipAddress,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700, display: "block" }}>
          Request to participate
        </Typography>
        <Typography component="span" variant="body2" color="text.secondary">
          {[auctionTitle, organiserName].filter(Boolean).join(" · ")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <BidderCompanyForm
          value={company}
          onChange={setCompany}
          errors={errors}
          onFieldEdit={clearError}
          intro="Your company details go to the organiser with your request. If you are approved, you accept the terms as usual."
        />

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: "#2D55EB", fontWeight: 600, mb: 1 }}>
            Lots I'm interested in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This helps the organiser; they decide which lots you can bid on.
          </Typography>
          <RadioGroup
            row
            value={allLots ? "all" : "choose"}
            onChange={(event) => {
              setAllLots(event.target.value === "all");
              setLotsError("");
            }}
          >
            <FormControlLabel value="all" control={<Radio />} label="All lots" />
            <FormControlLabel
              value="choose"
              control={<Radio />}
              label="Choose lots"
              disabled={sortedLots.length === 0}
            />
          </RadioGroup>
          {!allLots && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(4, 1fr)",
                },
                maxHeight: 220,
                overflowY: "auto",
                border: "1px solid #E3E7F0",
                p: 1,
              }}
            >
              {sortedLots.map((lot) => (
                <FormControlLabel
                  key={lot.eventItemId}
                  control={
                    <Checkbox
                      size="small"
                      checked={chosen.includes(lot.eventItemId)}
                      onChange={() => toggleLot(lot.eventItemId)}
                    />
                  }
                  label={`Lot ${lot.lotNumber}`}
                />
              ))}
            </Box>
          )}
          {lotsError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {lotsError}
            </Typography>
          )}

          <TextField
            label="Note to the organiser (optional)"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={2}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={send}
          disabled={sending}
          sx={{ backgroundColor: "#2D55EB", borderRadius: 0 }}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Send request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinRequestDialog;
